import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The width of the PAGE COLUMN, not of the window.
 *
 * The whole reason this surface was rebuilt is that it laid itself out against
 * the viewport while living inside an app shell with a sidebar. A viewport
 * breakpoint therefore says "desktop" while the column the card actually sits in
 * is phone-narrow — which is how a 1440px window still rendered a 697px-tall
 * card. Measuring the element removes the guess: the card asks how much room IT
 * has.
 *
 * ResizeObserver where it exists (every browser this platform supports), a
 * window resize listener as the fallback. A width of 0 means "not measured yet"
 * and is never treated as narrow — an unmeasured render must not flash the
 * compact layout at a desktop user.
 */
export const COMPACT_MAX_WIDTH = 700;

export const useMeasuredWidth = () => {
  const nodeRef = useRef(null);
  const [width, setWidth] = useState(0);

  const measure = useCallback(() => {
    const el = nodeRef.current;
    if (el) setWidth(el.clientWidth || 0);
  }, []);

  const ref = useCallback(
    (el) => {
      nodeRef.current = el;
      if (el) setWidth(el.clientWidth || 0);
    },
    [],
  );

  useEffect(() => {
    const el = nodeRef.current;
    if (!el) return undefined;

    if (typeof ResizeObserver === "function") {
      const observer = new ResizeObserver((entries) => {
        const measured = entries[0]?.contentRect?.width;
        setWidth(Math.round(measured ?? el.clientWidth ?? 0));
      });
      observer.observe(el);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return [ref, width];
};

export const isCompactWidth = (width) => width > 0 && width <= COMPACT_MAX_WIDTH;
